import { trackEvent } from "../utils/analytics";
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { getTopbarOffers } from "../api/topbar";
import { getReelFeed } from "../api/reel";
import { useWishlist } from "../utils/WishlistContext";
import { useCart } from "../utils/CartContext";

import BottomNavBar from "../components/BottomNavBar";

function useCategoriesScrollBtn() {
  const categoriesRowRef = useRef(null);
  const [showLeftBtn, setShowLeftBtn] = React.useState(false);
  const [showRightBtn, setShowRightBtn] = React.useState(false);

  const checkScrollBtns = () => {
    if (categoriesRowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoriesRowRef.current;
      setShowLeftBtn(scrollLeft > 5);
      setShowRightBtn(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };

  React.useEffect(() => {
    checkScrollBtns();
    window.addEventListener("resize", checkScrollBtns);
    return () => window.removeEventListener("resize", checkScrollBtns);
  }, []);

  const scrollCategoriesRight = () => {
    if (categoriesRowRef.current) {
      const container = categoriesRowRef.current;
      const itemWidth = container.children[0]?.offsetWidth || 100;
      const gap = 4; // gap-1 = 0.25rem ≈ 4px
      const scrollAmount = (itemWidth + gap) * 3; // Scroll 3 items

      container.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };
  const scrollCategoriesLeft = () => {
    if (categoriesRowRef.current) {
      const container = categoriesRowRef.current;
      const itemWidth = container.children[0]?.offsetWidth || 100;
      const gap = 4; // gap-1 = 0.25rem ≈ 4px
      const scrollAmount = (itemWidth + gap) * 3; // Scroll 3 items

      container.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return {
    categoriesRowRef,
    showLeftBtn,
    showRightBtn,
    checkScrollBtns,
    scrollCategoriesRight,
    scrollCategoriesLeft,
  };
}

function useDailyDealsScrollBtn() {
  const dailyDealsRowRef = useRef(null);
  const [showDailyDealsLeftBtn, setShowDailyDealsLeftBtn] =
    React.useState(false);
  const [showDailyDealsRightBtn, setShowDailyDealsRightBtn] =
    React.useState(false);

  const checkDailyDealsScrollBtns = () => {
    if (dailyDealsRowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = dailyDealsRowRef.current;
      setShowDailyDealsLeftBtn(scrollLeft > 5);
      setShowDailyDealsRightBtn(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };

  React.useEffect(() => {
    checkDailyDealsScrollBtns();
    window.addEventListener("resize", checkDailyDealsScrollBtns);
    return () =>
      window.removeEventListener("resize", checkDailyDealsScrollBtns);
  }, []);

  const scrollDailyDealsRight = () => {
    if (dailyDealsRowRef.current) {
      const container = dailyDealsRowRef.current;
      const itemWidth = container.children[0]?.offsetWidth || 200;
      const gap = 16; // gap-4 = 1rem = 16px
      const scrollAmount = (itemWidth + gap) * 2; // Scroll 2 items

      container.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };
  const scrollDailyDealsLeft = () => {
    if (dailyDealsRowRef.current) {
      const container = dailyDealsRowRef.current;
      const itemWidth = container.children[0]?.offsetWidth || 200;
      const gap = 16; // gap-4 = 1rem = 16px
      const scrollAmount = (itemWidth + gap) * 2; // Scroll 2 items

      container.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return {
    dailyDealsRowRef,
    showDailyDealsLeftBtn,
    showDailyDealsRightBtn,
    checkDailyDealsScrollBtns,
    scrollDailyDealsRight,
    scrollDailyDealsLeft,
  };
}

import CartDrawer from "../components/CartDrawer";
import Footer from "../components/Footer";
import { getCarousel } from "../api/catalog";
import * as blogApi from "../api/blog";
import logo from "../assets/images/logo.png";
import { useNavigate } from "react-router-dom";
import ReelPopup from "../components/ReelPopup";
import NewNavbar from "../components/newNavbar";
import DailyGiftFloatingIcon from "../components/DailyGiftFloatingIcon";
import CelebrationConfetti from "../components/CelebrationConfetti";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ShoppingCart,
  User,
  Search,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Heart,
  Star,
} from "lucide-react";

const FerreaFashion = () => {
  // Section refs for view tracking (must be inside component)
  const categoriesRef = useRef(null);
  const ourProductsRef = useRef(null);
  const shopTheLookRef = useRef(null);
  const trendingReelsRef = useRef(null);
  const blogsRef = useRef(null);
  const reviewsRef = useRef(null);
  const dailyDealsRef = useRef(null);

  // Cart context for add to cart functionality
  const { addToCart } = useCart();

  // Handler to add product to cart and open drawer
  const handleAddToCart = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    addToCart(product);
    window.dispatchEvent(new Event("open-cart-drawer"));
  };

  // Section view tracking for all main sections
  useEffect(() => {
    const sectionMap = [
      { ref: categoriesRef, name: "categories" },
      { ref: dailyDealsRef, name: "daily_deals" },
      { ref: ourProductsRef, name: "our_products" },
      { ref: shopTheLookRef, name: "shop_the_look" },
      { ref: trendingReelsRef, name: "trending_reels" },
      { ref: blogsRef, name: "blogs" },
      { ref: reviewsRef, name: "reviews" },
    ];
    const viewObservers = [];
    const engagedObservers = [];
    const engagedTimers = {};
    const engagedFired = {};
    sectionMap.forEach(({ ref, name }) => {
      if (!ref.current) return;
      // Only fire section_view for daily_deals
      if (name === "daily_deals") {
        const viewObserver = new window.IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                trackEvent("section_view", { section: name });
                viewObserver.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.5 },
        );
        viewObserver.observe(ref.current);
        viewObservers.push(viewObserver);
      }
      // Section engaged observer (fires if in view 5s, only once)
      const engagedObserver = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !engagedFired[name]) {
              engagedTimers[name] = setTimeout(() => {
                if (!engagedFired[name]) {
                  trackEvent("section_engaged", { section: name });
                  engagedFired[name] = true;
                }
              }, 5000);
            } else {
              clearTimeout(engagedTimers[name]);
            }
          });
        },
        { threshold: name === "daily_deals" ? 0.6 : 0.3 },
      );
      engagedObserver.observe(ref.current);
      engagedObservers.push(engagedObserver);
    });
    return () => {
      viewObservers.forEach((o) => o && o.disconnect());
      engagedObservers.forEach((o) => o && o.disconnect());
      Object.values(engagedTimers).forEach((timer) => clearTimeout(timer));
    };
  }, []);
  // Section view tracking for all main sections
  useEffect(() => {
    const sectionMap = [
      { ref: categoriesRef, name: "categories" },
      { ref: dailyDealsRef, name: "daily_deals" },
      { ref: ourProductsRef, name: "our_products" },
      { ref: shopTheLookRef, name: "shop_the_look" },
      { ref: trendingReelsRef, name: "trending_reels" },
      { ref: blogsRef, name: "blogs" },
      { ref: reviewsRef, name: "reviews" },
    ];
    const observers = sectionMap.map(({ ref, name }) => {
      if (!ref.current) return null;
      const observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              trackEvent("section_view", { section: name });
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 },
      );
      observer.observe(ref.current);
      return observer;
    });
    return () => observers.forEach((o) => o && o.disconnect());
  }, []);
  // ReelPopup state
  const [reelPopupOpen, setReelPopupOpen] = useState(true); // Show on load
  const [selectedReelId, setSelectedReelId] = useState(null);

  // Removed auto-close: popup stays until user closes it
  const [currentSlide, setCurrentSlide] = useState(0);
  const [promoOpen, setPromoOpen] = useState(false);
  const [giftBouncing, setGiftBouncing] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [activeProductTab, setActiveProductTab] = useState("best-seller");
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [reelsStartIndex, setReelsStartIndex] = useState(0);
  const [trendingStartIndex, setTrendingStartIndex] = useState(0);
  const [reelsLikes, setReelsLikes] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
  const [shareOpen, setShareOpen] = useState(null);
  const { isInWishlist, toggleWishlist } = useWishlist();
  // Reels state for trending reels section
  const [reels, setReels] = useState([]);
  const [reelsLoading, setReelsLoading] = useState(true);

  useEffect(() => {
    async function fetchReels() {
      try {
        const data = await getReelFeed(1, "lifestyle");
        setReels(Array.isArray(data.reels) ? data.reels : []);
      } catch (err) {
        setReels([]);
      } finally {
        setReelsLoading(false);
      }
    }
    fetchReels();
  }, []);

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogStartIndex, setBlogStartIndex] = useState(0);
  const blogsPerView = 4;

  const navigate = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);

  // Shop the Look dynamic data (Video-based)
  const [shopTheLook, setShopTheLook] = useState(null);
  const [shopTheLookLoading, setShopTheLookLoading] = useState(true);

  // Shop the Look Image dynamic data (Image-based)
  const [shopTheLookImage, setShopTheLookImage] = useState(null);
  const [shopTheLookImageLoading, setShopTheLookImageLoading] = useState(true);

  // Shop the Look show more state for mobile
  const [shopTheLookShowAll, setShopTheLookShowAll] = useState(false);
  const [shopTheLookImageShowAll, setShopTheLookImageShowAll] = useState(false);

  // Carousel slides fetched from backend
  const [carouselSlides, setCarouselSlides] = useState([]);

  useEffect(() => {
    async function fetchSlides() {
      try {
        const slides = await getCarousel();
        setCarouselSlides(slides);
      } catch (err) {
        setCarouselSlides([]);
      }
    }
    fetchSlides();
  }, []);

  // Fetch Shop the Look configuration (Video-based)
  useEffect(() => {
    async function fetchShopTheLook() {
      try {
        const apiUrl = process.env.REACT_APP_API_BASE || "/api";
        const res = await axios.get(`${apiUrl}/shop-the-look/active`);
        setShopTheLook(res.data);
      } catch (err) {
        console.log("Shop the Look not configured yet");
        setShopTheLook(null);
      } finally {
        setShopTheLookLoading(false);
      }
    }
    fetchShopTheLook();

    // Refresh Shop the Look every 30 seconds to show product updates
    const interval = setInterval(fetchShopTheLook, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Shop the Look Image configuration (Image-based)
  useEffect(() => {
    async function fetchShopTheLookImage() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "/api";
        const res = await axios.get(`${apiUrl}/shop-the-look-image/active`);
        setShopTheLookImage(res.data);
      } catch (err) {
        console.log("Shop the Look Image not configured yet");
        setShopTheLookImage(null);
      } finally {
        setShopTheLookImageLoading(false);
      }
    }
    fetchShopTheLookImage();

    // Refresh Shop the Look Image every 30 seconds to show product updates
    const interval = setInterval(fetchShopTheLookImage, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch categories from backend
  const [categories, setCategories] = useState([]);
  const {
    categoriesRowRef,
    showLeftBtn,
    showRightBtn,
    checkScrollBtns,
    scrollCategoriesRight,
    scrollCategoriesLeft,
  } = useCategoriesScrollBtn();
  const {
    dailyDealsRowRef,
    showDailyDealsLeftBtn,
    showDailyDealsRightBtn,
    checkDailyDealsScrollBtns,
    scrollDailyDealsRight,
    scrollDailyDealsLeft,
  } = useDailyDealsScrollBtn();
  useEffect(() => {
    async function fetchCategories() {
      try {
        const cats = await import("../api/catalog").then((mod) =>
          mod.listCategories(),
        );
        setCategories(Array.isArray(cats) ? cats : []);
        setTimeout(checkScrollBtns, 100);
      } catch (e) {
        setCategories([]);
      }
    }
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  // Real products fetched from backend
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE || "/api";

    const fetchProducts = () => {
      fetch(API_BASE + "/products")
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            setProducts(data);
          } else if (Array.isArray(data.products)) {
            setProducts(data.products);
          } else {
            setProducts([]);
          }
          setTimeout(checkDailyDealsScrollBtns, 100);
        })
        .catch((err) => console.error("Failed to fetch products", err));
    };
    fetchProducts();
    // Refresh products every 30 seconds to show updates from admin panel
    const interval = setInterval(fetchProducts, 30000);
    return () => clearInterval(interval);
  }, []);
  // Helper to calculate discount percent
  function getDiscountPercent(original, sale) {
    return Math.round(((original - sale) / original) * 100);
  }
  // Example rendering for featured products (update this section in your render/return):

  {
    /* Real Products Section */
  }
  <div style={{ display: "flex", gap: 24, margin: "24px 0" }}>
    {products.length === 0 ? (
      <div>Loading products...</div>
    ) : (
      products.map((product) => (
        <Link
          to={`/product/${product._id}`}
          key={product._id}
          style={{ textDecoration: "none", color: "inherit" }}
          onClick={() =>
            trackEvent("product_click", {
              product_id: product._id,
              product_name: product.name,
              section: "featured",
            })
          }
        >
          <div
            style={{
              width: 220,
              border: "1px solid #eee",
              borderRadius: 8,
              padding: 12,
              background: "#fff",
              boxShadow: "0 2px 8px #f3f3f3",
              cursor: "pointer",
              transition: "box-shadow 0.2s",
            }}
          >
            <img
              src={
                product.imageUrl ||
                product.image ||
                "https://via.placeholder.com/220x220?text=No+Image"
              }
              alt={product.name}
              style={{
                width: "100%",
                height: 220,
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
            <div style={{ marginTop: 10, marginBottom: 4, fontWeight: 600 }}>
              {product.name}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  color: "#888",
                  textDecoration: "line-through",
                  fontSize: 15,
                }}
              >
                ₹{product.originalPrice}
              </span>
              <span style={{ color: "#222", fontWeight: 700, fontSize: 17 }}>
                ₹{product.salePrice}
              </span>
              {product.originalPrice && product.salePrice && (
                <span
                  style={{ color: "#e53935", fontWeight: 600, fontSize: 14 }}
                >
                  {getDiscountPercent(product.originalPrice, product.salePrice)}
                  % OFF
                </span>
              )}
            </div>
            <div style={{ color: "#555", fontSize: 14, marginBottom: 6 }}>
              ★{product.rating || 5}
            </div>
            <button
              onClick={(e) => handleAddToCart(product, e)}
              style={{
                padding: "6px 18px",
                border: "1px solid #222",
                borderRadius: 20,
                background: "#fff",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Add to Cart
            </button>
          </div>
        </Link>
      ))
    )}
  </div>;

  const beddingProducts = [
    {
      id: 1,
      name: "Linen Sheet Set",
      price: "From $89.90",
      image:
        "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400&h=400&fit=crop",
    },
    {
      id: 2,
      name: "Organic Cotton Sheet Set",
      price: "From $79.90",
      image:
        "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=400&h=400&fit=crop",
    },
    {
      id: 3,
      name: "European Linen Pillowcases",
      price: "From $39.90",
      image:
        "https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=400&h=400&fit=crop",
    },
    {
      id: 4,
      name: "Linen Lumbar Pillow",
      price: "From $64.90",
      image:
        "https://images.unsplash.com/photo-1616627976394-bdf965aa7a48?w=400&h=400&fit=crop",
    },
    {
      id: 5,
      name: "Organic Cotton Down Duvet",
      price: "From $99.90",
      image:
        "https://images.unsplash.com/photo-1631049552057-403cdb8f0658?w=400&h=400&fit=crop",
    },
    {
      id: 6,
      name: "Cable Knit Throw",
      price: "$159.90",
      image:
        "https://images.unsplash.com/photo-1585909695284-32d2985ac9c0?w=400&h=400&fit=crop",
    },
  ];

  const winterProducts = [
    {
      id: 1,
      name: "Women's Cashmere Coat",
      price: "$600.00",
      image:
        "https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=400&h=500&fit=crop",
    },
    {
      id: 2,
      name: "Cashmere Gloves",
      price: "$29.90",
      image:
        "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=500&fit=crop",
    },
    {
      id: 3,
      name: "Men's Merino Henley",
      price: "$79.90",
      image:
        "https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=400&h=500&fit=crop",
    },
    {
      id: 4,
      name: "Ribbed Cashmere Beanie",
      price: "$34.90",
      image:
        "https://images.unsplash.com/photo-1576871337622-98d48d1cf531?w=400&h=500&fit=crop",
    },
    {
      id: 5,
      name: "Women's Down Coat",
      price: "$200.00",
      image:
        "https://images.unsplash.com/photo-1539533113208-f6df8cc8b543?w=400&h=500&fit=crop",
    },
    {
      id: 6,
      name: "Cashmere Wrap",
      price: "$54.00",
      image:
        "https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=400&h=500&fit=crop",
    },
  ];

  // Promo images from backend
  const [promoImages, setPromoImages] = useState([]);
  useEffect(() => {
    async function fetchPromoImages() {
      try {
        const promos = await import("../api/catalog").then((mod) =>
          mod.getPromo(),
        );
        setPromoImages(promos);
      } catch (e) {
        setPromoImages([]);
      }
    }
    fetchPromoImages();
  }, []);

  // Carousel logic for promo images
  const [currentPromo, setCurrentPromo] = useState(0);
  useEffect(() => {
    if (!promoImages.length) return;
    const timer = setInterval(() => {
      setCurrentPromo((prev) => (prev + 1) % promoImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [promoImages]);

  const reelsVideos = [
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
    "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4",
  ];

  // Topbar offers fetched from backend
  const [topbarOffers, setTopbarOffers] = useState([]);

  // Auto-scroll hero images (future-proof)
  useEffect(() => {
    if (!carouselSlides.length) return;

    const heroTimer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 2500);

    return () => clearInterval(heroTimer);
  }, [carouselSlides]);

  // Fetch topbar offers from backend
  useEffect(() => {
    getTopbarOffers().then((offers) => {
      setTopbarOffers(offers.map((o) => o.offerText));
    });
  }, []);

  // Fetch blogs from backend
  useEffect(() => {
    async function fetchBlogs() {
      try {
        const response = await blogApi.getAllBlogs({
          limit: 100,
          status: "published",
        });
        if (response.success && Array.isArray(response.data)) {
          setBlogs(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch blogs:", error);
        setBlogs([]);
      }
    }
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (!topbarOffers.length) return;
    const promoTimer = setInterval(() => {
      setCurrentPromoIndex((prev) => (prev + 1) % topbarOffers.length);
    }, 4000);
    return () => clearInterval(promoTimer);
  }, [topbarOffers]);

  const nextSlide = () => {
    if (!carouselSlides.length) return;
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };

  const prevSlide = () => {
    if (!carouselSlides.length) return;
    setCurrentSlide(
      (prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length,
    );
  };

  // Auto-play reels when trending section is visible
  useEffect(() => {
    const section = document.getElementById("trending-reels-section");
    if (!section) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Play all reel videos in trending section
            const videos = section.querySelectorAll("video");
            videos.forEach((video) => {
              if (video.paused) video.play();
            });
          } else {
            // Pause all reel videos when section is not visible
            const videos = section.querySelectorAll("video");
            videos.forEach((video) => {
              if (!video.paused) video.pause();
            });
          }
        });
      },
      { threshold: 0.2 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [reels]);

  return (
    <div>
      {reelPopupOpen && <ReelPopup onClose={() => setReelPopupOpen(false)} />}

<Helmet>

  {/* Primary SEO */}
  <title>Ferrea Fashion® | Cosmetics, Makeup & Beauty Products Online</title>

  <meta
    name="description"
    content="Ferrea Fashion® offers premium cosmetics, matte lipsticks, skincare and beauty essentials. Discover high-quality makeup products designed for everyday beauty."
  />

  <meta
    name="keywords"
    content="Ferrea Fashion, cosmetics online, matte lipstick, beauty products, makeup products, lipstick online, skincare, Ferrea Fashion cosmetics"
  />

  <meta name="robots" content="index, follow" />
  <meta name="author" content="Ferrea Fashion" />

  {/* Canonical URL */}
  <link rel="canonical" href="https://ferreafashion.com/" />

  {/* Open Graph (social sharing SEO) */}
  <meta property="og:type" content="website" />
  <meta property="og:title" content="Ferrea Fashion® | Premium Cosmetics & Makeup" />
  <meta
    property="og:description"
    content="Shop premium cosmetics, lipsticks and beauty products from Ferrea Fashion."
  />
  <meta property="og:url" content="https://ferreafashion.com/" />
  <meta property="og:image" content="https://ferreafashion.com/images/social-preview.jpg" />

  {/* Twitter Preview */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Ferrea Fashion® | Cosmetics & Makeup" />
  <meta
    name="twitter:description"
    content="Discover luxury cosmetics, matte lipsticks and beauty essentials."
  />
  <meta name="twitter:image" content="https://ferreafashion.com/images/social-preview.jpg" />

  {/* Mobile SEO */}
  <meta name="viewport" content="width=device-width, initial-scale=1" />

  {/* Brand Structured Data */}
  <script type="application/ld+json">
    {`
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Ferrea Fashion",
      "url": "https://ferreafashion.com",
      "logo": "https://ferreafashion.com/logo.png",
      "sameAs": [
        "https://instagram.com/ferreafashion",
        "https://facebook.com/ferreafashion"
      ]
    }
    `}
  </script>

</Helmet>


      <section className="min-h-screen bg-white">
        {/* Topbar */}
        <div className="bg-pink-500 text-white py-2 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() =>
                  setCurrentPromoIndex(
                    (prev) =>
                      (prev - 1 + topbarOffers.length) % topbarOffers.length,
                  )
                }
                className="hover:opacity-70"
              >
                <ChevronLeft size={20} />
              </button>
              <p className="text-sm font-semibold tracking-wide">
                {topbarOffers[currentPromoIndex]}
              </p>
              <button
                onClick={() =>
                  setCurrentPromoIndex(
                    (prev) => (prev + 1) % topbarOffers.length,
                  )
                }
                className="hover:opacity-70"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Navbar */}
        <NewNavbar />

        {/* Reel Popup - Top Right Corner */}
        {reelPopupOpen && (
          <ReelPopup
            reelId={selectedReelId}
            onClose={() => setReelPopupOpen(false)}
          />
        )}

        {/* Hero Section */}
        <DailyGiftFloatingIcon
          bouncing={giftBouncing}
          onClick={() => {
            setPromoOpen(true);
            setShowConfetti(true);
            setGiftBouncing(false);
            setTimeout(() => setShowConfetti(false), 1800);
          }}
        />
        {(promoOpen || showConfetti) && <CelebrationConfetti show={true} />}
        {promoOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xs flex flex-col items-center relative">
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-pink-500 text-xl font-bold focus:outline-none"
                onClick={() => setPromoOpen(false)}
                aria-label="Close"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
              <h2 className="text-xl font-bold mb-2 text-center text-pink-500">
                🎁 Your Daily Gift!
              </h2>
              <div className="text-gray-700 text-center mb-4">
                Use this promo code for a special discount:
              </div>
              <div className="mb-4 flex flex-col items-center">
                <span
                  className={`font-mono text-lg bg-gray-100 px-4 py-2 rounded border border-pink-300 select-all cursor-pointer transition-colors ${
                    copied ? "bg-pink-100 border-pink-500 text-pink-600" : ""
                  }`}
                  onClick={() => {
                    navigator.clipboard.writeText("DAILY2026");
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1200);
                  }}
                  title="Click to copy"
                >
                  {copied ? "Copied!" : "DAILY2026"}
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  Click code to copy
                </span>
              </div>
            </div>
          </div>
        )}
        <div className="relative bg-gray-100">
          <div className="w-full">
            <div className="hidden md:block relative h-96 lg:h-[500px]">
              <div
                className="absolute inset-0 bg-cover bg-center transition-all duration-500"
                style={{
                  backgroundImage: `url(${carouselSlides[currentSlide]?.imagePath})`,
                }}
              >
                {/* Hero overlay removed */}
              </div>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 border border-gray-800 rounded-full px-6 py-2 font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-all shadow-none"
              >
                <ChevronLeft size={24} className="text-gray-800" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 border border-gray-800 rounded-full px-6 py-2 font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-all shadow-none"
              >
                <ChevronRight size={24} className="text-gray-800" />
              </button>
              <div className="flex items-center gap-2 absolute bottom-4 left-1/2 -translate-x-1/2">
                {carouselSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      currentSlide === index
                        ? "bg-white w-6"
                        : "bg-white bg-opacity-50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Hero */}
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex">
              {carouselSlides.map((slide, index) => (
                <div
                  key={index}
                  className="min-w-full h-72 sm:h-80 relative flex-shrink-0"
                  style={{
                    backgroundImage: `url(${slide.imagePath})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {/* Mobile hero overlay removed */}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* USP Marquee Section - Improved */}
        <div
          className="w-full bg-white border-t border-b border-gray-200 relative overflow-hidden h-12 usp-marquee-thin"
          style={{ minHeight: "48px" }}
        >
          {/* Gradient fades */}
          <div
            className="absolute left-0 top-0 h-full w-16 pointer-events-none"
            style={{
              background: "linear-gradient(to right, white 60%, transparent)",
            }}
          />
          <div
            className="absolute right-0 top-0 h-full w-16 pointer-events-none"
            style={{
              background: "linear-gradient(to left, white 60%, transparent)",
            }}
          />
          {/* Marquee content */}
          <div
            className="whitespace-nowrap flex items-center gap-8 animate-marquee h-full"
            style={{ animation: "marquee 22s linear infinite", height: "100%" }}
          >
            {/* Repeat for seamless loop */}
            {[1, 2].map((loop) => (
              <React.Fragment key={loop}>
                <span className="flex items-center gap-2 mx-4 font-semibold text-gray-700 text-sm h-full">
                  <Star className="text-yellow-500 w-4 h-4" />
                  High quality products
                </span>
                <span className="flex items-center gap-2 mx-4 font-semibold text-gray-700 text-sm">
                  <ChevronRight className="text-blue-500 w-4 h-4" />
                  Fast & reliable shipping
                </span>
                <span className="flex items-center gap-2 mx-4 font-semibold text-gray-700 text-sm">
                  <X className="text-pink-500 w-4 h-4" />
                  Easy returns & exchanges
                </span>
                <span className="flex items-center gap-2 mx-4 font-semibold text-gray-700 text-sm">
                  <User className="text-green-500 w-4 h-4" />
                  24/7 customer support
                </span>
                <span className="flex items-center gap-2 mx-4 font-semibold text-gray-700 text-sm">
                  <Heart className="text-red-500 w-4 h-4" />
                  Exclusive daily deals
                </span>
                <span className="flex items-center gap-2 mx-4 font-semibold text-gray-700 text-sm">
                  <ShoppingCart className="text-purple-500 w-4 h-4" />
                  Secure payments
                </span>
              </React.Fragment>
            ))}
          </div>
          <style>{`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            /* Responsive: Make USP Marquee a bit taller on mobile/tablet */
            @media (max-width: 1024px) {
              .usp-marquee-thin {
                height: 36px !important;
                min-height: 0 !important;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
              .usp-marquee-thin .animate-marquee {
                height: 36px !important;
                min-height: 0 !important;
              }
              .usp-marquee-thin span {
                font-size: 13px !important;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
            }
            @media (min-width: 1025px) {
              .usp-marquee-thin {
                height: 48px !important;
                min-height: 48px !important;
                display: flex;
                align-items: center;
              }
              .usp-marquee-thin .animate-marquee {
                height: 48px !important;
                min-height: 48px !important;
                display: flex;
                align-items: center;
              }
            }
          `}</style>
        </div>

        {/* Categories Section */}
        {categories.length > 0 && (
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6"
            ref={categoriesRef}
          >
            <div className="flex justify-between items-center mb-3 md:mb-4">
              <h2 className="font-normal uppercase tracking-wide text-base md:text-lg lg:text-2xl text-gray-900">
                SHOP BY CATEGORIES
              </h2>
              <Link
                to="/categories"
                className="border border-gray-800 rounded-full px-4 py-1.5 font-semibold text-sm text-gray-900 bg-white hover:bg-pink-500 hover:text-white hover:border-pink-500 transition-all flex items-center"
              >
                View All <ChevronRight size={16} className="ml-1" />
              </Link>
            </div>
            <div className="relative">
              <div className="relative">
                {showLeftBtn && (
                  <button
                    className="absolute left-0 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full shadow p-2 z-10 hover:bg-gray-100 transition"
                    style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                    onClick={scrollCategoriesLeft}
                    aria-label="Scroll categories left"
                  >
                    <ChevronLeft size={20} className="md:w-6 md:h-6" />
                  </button>
                )}
                <div
                  className="flex gap-1 md:gap-3 lg:gap-4 overflow-x-auto pb-2 no-scrollbar"
                  style={{ scrollbarWidth: "none" }}
                  ref={categoriesRowRef}
                  onScroll={checkScrollBtns}
                >
                  {categories.map((category, index) => (
                    <div
                      key={category._id || index}
                      className="flex flex-col items-center min-w-[calc(33.333%-2px)] md:min-w-[140px] lg:min-w-[140px]"
                      onClick={() =>
                        trackEvent("category_click", {
                          category_id: category._id,
                          category_name: category.name,
                        })
                      }
                    >
                      <div className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full bg-gray-100 flex items-center justify-center mb-3 shadow-sm overflow-hidden hover:shadow-lg transition hover:scale-105">
                        <img
                          src={category.photo}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span
                        className="font-bold text-xs md:text-sm lg:text-base text-gray-900 mt-2 text-center line-clamp-2"
                        style={{ whiteSpace: "normal" }}
                      >
                        {category.name}
                      </span>
                    </div>
                  ))}
                </div>
                {showRightBtn && (
                  <button
                    className="absolute right-0 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full shadow p-2 z-10 hover:bg-gray-100 transition"
                    style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                    onClick={scrollCategoriesRight}
                    aria-label="Scroll categories right"
                  >
                    <ChevronRight size={20} className="md:w-6 md:h-6" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Featured Products Section from Code 2 */}
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
          id="trending-reels-section"
          ref={dailyDealsRef}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900">
              DAILY DEALS
            </h3>
            <Link
              to="/products"
              className="border border-gray-800 rounded-full px-4 py-1.5 font-semibold text-sm text-gray-900 bg-white hover:bg-pink-500 hover:text-white hover:border-pink-500 transition-all flex items-center"
            >
              View All <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>

          <div className="relative">
            {showDailyDealsLeftBtn && (
              <button
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full shadow p-2 z-10 hover:bg-gray-100 transition"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                onClick={scrollDailyDealsLeft}
                aria-label="Scroll daily deals left"
              >
                <ChevronLeft size={20} className="md:w-6 md:h-6" />
              </button>
            )}
            <div
              className="overflow-x-auto pb-2 no-scrollbar"
              style={{ scrollbarWidth: "none" }}
              ref={dailyDealsRowRef}
              onScroll={checkDailyDealsScrollBtns}
            >
              <div className="flex gap-4 md:gap-6 lg:gap-8">
                {products.length === 0 ? (
                  <div>Loading products...</div>
                ) : (
                  products.map((product) => {
                    const colors = [
                      "#222",
                      "#888",
                      "#ccc",
                      "#e0cfc3",
                      "#bfa083",
                      "#a67c52",
                    ];
                    const maxShow = 2;
                    const shownColors = colors.slice(0, maxShow);
                    const remaining = colors.length - maxShow;
                    const name =
                      product.name ||
                      product.display?.name ||
                      product.basic?.productName ||
                      "";
                    const image =
                      product.photo ||
                      product.display?.mainImage ||
                      product.media?.mainImage ||
                      "https://via.placeholder.com/220x220?text=No+Image";
                    const price =
                      product.price ||
                      product.display?.price ||
                      product.pricing?.price ||
                      "";
                    const discountedPrice =
                      product.discountedPrice ||
                      product.display?.discountedPrice ||
                      product.pricing?.discountedPrice ||
                      "";
                    const rating =
                      product.rating || product.display?.rating || "";
                    const tag =
                      Array.isArray(product.segments) &&
                      product.segments.length > 0
                        ? product.segments[0]
                        : null;

                    return (
                      <Link
                        to={`/product/${
                          product.slug ||
                          product.seo?.urlSlug ||
                          product._id ||
                          product.id
                        }`}
                        key={product._id || product.id}
                        style={{ textDecoration: "none", color: "inherit" }}
                        onClick={() =>
                          trackEvent("product_click", {
                            product_id: product._id,
                            product_name: product.name,
                            section: "daily_deals",
                          })
                        }
                      >
                        <div className="group cursor-pointer w-40 sm:w-48 md:w-56 lg:w-64 flex-shrink-0">
                          <div className="relative overflow-hidden mb-3 bg-gray-50 aspect-[3/4]">
                            {tag && (
                              <span
                                className="absolute top-2 left-2 z-10 text-white font-semibold flex items-center gap-1 uppercase"
                                style={{
                                  background: "rgba(0,0,0,0.68)",
                                  fontSize: "10px",
                                  padding: "2px 7px",
                                  borderRadius: "6px",
                                  letterSpacing: 0.5,
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "11px",
                                    marginRight: "2px",
                                  }}
                                  role="img"
                                  aria-label="tag"
                                >
                                  🔥
                                </span>
                                {tag}
                              </span>
                            )}
                            <img
                              src={image}
                              alt={name}
                              className="w-full h-full object-cover group-hover:scale-105 transition duration-500 rounded"
                            />
                            <button
                              className="absolute top-3 right-3"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleWishlist(product);
                              }}
                            >
                              <span
                                style={{
                                  position: "relative",
                                  display: "inline-block",
                                  width: 26,
                                  height: 26,
                                }}
                              >
                                <span
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    width: 26,
                                    height: 26,
                                    background: "white",
                                    borderRadius: "50%",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                    zIndex: 1,
                                  }}
                                />
                                <Heart
                                  className={`w-4 h-4 ${
                                    isInWishlist(product._id || product.id)
                                      ? "fill-pink-500 text-pink-500"
                                      : "text-gray-400"
                                  }`}
                                  strokeWidth={1.5}
                                  style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    zIndex: 2,
                                  }}
                                />
                              </span>
                            </button>
                          </div>
                          <h4 className="text-xs font-normal text-gray-900 line-clamp-1 w-full">
                            {name}
                          </h4>
                          <div className="flex flex-col w-full mt-1 mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 line-through">
                                ₹{price}
                              </span>
                              <span className="text-sm text-gray-900 font-bold">
                                ₹{discountedPrice}
                              </span>
                              {price && discountedPrice && (
                                <span className="text-xs text-red-600 font-semibold">
                                  {getDiscountPercent(price, discountedPrice)}%
                                  OFF
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex gap-0.5 items-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className="w-3 h-3 text-black"
                                  fill="none"
                                  stroke="#000"
                                />
                              ))}
                              <span className="text-xs ml-1">
                                {rating || "5"}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center mt-1">
                            <button 
                              className="border border-gray-800 rounded-full px-3 py-1 text-xs"
                              onClick={(e) => handleAddToCart(product, e)}
                            >
                              Add to Cart
                            </button>
                            <div className="flex items-center">
                              {shownColors.map((c, i) => (
                                <span
                                  key={i}
                                  className="w-5 h-5 rounded-full border mr-1"
                                  style={{ backgroundColor: c }}
                                />
                              ))}
                              {remaining > 0 && (
                                <span className="text-[10px] text-gray-500">
                                  +{remaining}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })
                )}
              </div>
            </div>
            {showDailyDealsRightBtn && (
              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full shadow p-2 z-10 hover:bg-gray-100 transition"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                onClick={scrollDailyDealsRight}
                aria-label="Scroll daily deals right"
              >
                <ChevronRight size={20} className="md:w-6 md:h-6" />
              </button>
            )}
          </div>
        </section>

        {/* OUR PRODUCTS */}
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
          ref={ourProductsRef}
        >
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900">
              OUR PRODUCTS
            </h3>
            <Link
              to="/products"
              className="border border-gray-800 rounded-full px-4 py-1.5 font-semibold text-sm text-gray-900 bg-white hover:bg-pink-500 hover:text-white hover:border-pink-500 transition-all flex items-center"
            >
              View All <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>

          {/* Tabs */}
          {/* Tabs */}
          <div
            className="flex gap-4 md:gap-8 border-b mb-3 overflow-x-auto pb-2"
            style={{ scrollbarWidth: "none" }}
          >
            {["best-seller", "on-sale", "newly-arrived", "expensive"].map(
              (tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveProductTab(tab)}
                  className={`pb-2 font-semibold uppercase text-xs md:text-base whitespace-nowrap transition-colors ${
                    activeProductTab === tab
                      ? "border-b-2 border-gray-800 text-gray-800"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.replace("-", " ")}
                </button>
              ),
            )}
          </div>

          {/* GRID */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {(activeProductTab === "best-seller"
              ? products.slice(0, 8)
              : activeProductTab === "on-sale"
                ? products.slice(2, 6)
                : activeProductTab === "newly-arrived"
                  ? products.slice(4, 8)
                  : products.slice(0, 4)
            ).map((product) => {
              // Use correct backend fields
              const colors = [
                "#222",
                "#888",
                "#ccc",
                "#e0cfc3",
                "#bfa083",
                "#a67c52",
              ];
              const shownColors = colors.slice(0, 2);
              const remaining = colors.length - 2;
              const name =
                product.name ||
                product.display?.name ||
                product.basic?.productName ||
                "";
              const image =
                product.photo ||
                product.display?.mainImage ||
                product.media?.mainImage ||
                "https://via.placeholder.com/220x220?text=No+Image";
              const price =
                product.price ||
                product.display?.price ||
                product.pricing?.price ||
                "";
              const discountedPrice =
                product.discountedPrice ||
                product.display?.discountedPrice ||
                product.pricing?.discountedPrice ||
                "";
              const rating = product.rating || product.display?.rating || "";
              // Use first segment as tag if available
              const tag =
                Array.isArray(product.segments) && product.segments.length > 0
                  ? product.segments[0]
                  : null;

              return (
                <Link
                  to={`/product/${
                    product.slug ||
                    product.seo?.urlSlug ||
                    product._id ||
                    product.id
                  }`}
                  key={product._id || product.id}
                  style={{ textDecoration: "none", color: "inherit" }}
                  onClick={() =>
                    trackEvent("product_click", {
                      product_id: product._id,
                      product_name: product.name,
                      section: activeProductTab,
                    })
                  }
                >
                  <div className="group cursor-pointer w-40 sm:w-48 md:w-56 lg:w-64 mx-auto">
                    <div className="relative overflow-hidden mb-3 bg-gray-50 aspect-[3/4]">
                      {/* Tag */}
                      {tag && (
                        <span
                          className="absolute top-2 left-2 z-10 text-white font-semibold flex items-center gap-1 uppercase"
                          style={{
                            background: "rgba(0,0,0,0.68)",
                            fontSize: "10px",
                            padding: "2px 7px",
                            borderRadius: "6px",
                            letterSpacing: 0.5,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
                          }}
                        >
                          <span
                            style={{ fontSize: "11px", marginRight: "2px" }}
                            role="img"
                            aria-label="tag"
                          >
                            🔥
                          </span>
                          {tag}
                        </span>
                      )}
                      <img
                        src={image}
                        alt={name}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500 rounded"
                      />
                      <button
                        className="absolute top-3 right-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWishlist(product);
                        }}
                      >
                        <span
                          style={{
                            position: "relative",
                            display: "inline-block",
                            width: 26,
                            height: 26,
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              width: 26,
                              height: 26,
                              background: "white",
                              borderRadius: "50%",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                              zIndex: 1,
                            }}
                          />
                          <Heart
                            className={`w-4 h-4 ${
                              isInWishlist(product._id || product.id)
                                ? "fill-pink-500 text-pink-500"
                                : "text-gray-400"
                            }`}
                            strokeWidth={1.5}
                            style={{
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              zIndex: 2,
                            }}
                          />
                        </span>
                      </button>
                    </div>
                    <h4 className="text-xs font-normal text-gray-900 line-clamp-1">
                      {name}
                    </h4>
                    <div className="flex flex-col w-full mt-1 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 line-through">
                          ₹{price}
                        </span>
                        <span className="text-sm text-gray-900 font-bold">
                          ₹{discountedPrice}
                        </span>
                        {price && discountedPrice && (
                          <span className="text-xs text-red-600 font-semibold">
                            {getDiscountPercent(price, discountedPrice)}% OFF
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex gap-0.5 items-center">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className="w-3 h-3 text-black"
                            fill="none"
                            stroke="#000"
                          />
                        ))}
                        <span className="text-xs ml-1">{rating || "5"}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <button 
                        className="border border-gray-800 rounded-full px-3 py-1 text-xs"
                        onClick={(e) => handleAddToCart(product, e)}
                      >
                        Add to Cart
                      </button>
                      <div className="flex items-center">
                        {shownColors.map((c, i) => (
                          <span
                            key={i}
                            className="w-5 h-5 rounded-full border mr-1"
                            style={{ backgroundColor: c }}
                          />
                        ))}
                        {remaining > 0 && (
                          <span className="text-[10px] text-gray-500">
                            +{remaining}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* A Shop the Look Section (Dynamic) */}
        {shopTheLook &&
          shopTheLook.reelId &&
          shopTheLook.productIds?.length > 0 && (
            <section className="bg-stone-100 py-8" ref={shopTheLookRef}>
              <div className="max-w-7xl mx-auto px-4">
                <h2 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900 mb-6">
                  SHOP THE LOOK
                </h2>
                {/* MAIN ROW */}
                <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1.35fr] gap-12 items-stretch">
                  {/* LEFT : VIDEO (vertical reel style) */}
                  <div className="relative w-[300px] md:w-[320px] h-[480px] md:h-[640px] overflow-hidden rounded-xl flex items-center justify-center mx-auto">
                    <video
                      src={shopTheLook.reelId.videoUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      style={{ aspectRatio: "9/16" }}
                    />

                    {/* OVERLAY */}
                    <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 text-white bg-gradient-to-t from-black/40 to-transparent">
                      <h3 className="text-2xl md:text-4xl font-light mb-2">
                        {shopTheLook.title || "A softer start"}
                      </h3>
                      <p className="text-sm mb-6 tracking-wide">
                        {shopTheLook.subtitle ||
                          "The luxury bedding upgrade you deserve."}
                      </p>
                      <a
                        href={shopTheLook.ctaLink || "#"}
                        className="bg-orange-400 w-fit text-white px-8 py-3 text-xs tracking-widest uppercase font-semibold hover:bg-orange-500 transition rounded-full"
                      >
                        {shopTheLook.ctaText || "SHOP BEDDING"}
                      </a>
                    </div>
                  </div>

                  {/* RIGHT : PRODUCTS (bigger images) */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-10 h-auto md:h-[560px] content-start md:content-between">
                    {shopTheLook.productIds.map((product, index) => {
                      const name =
                        product.basic?.productName || product.name || "Product";
                      const image =
                        product.media?.mainImage || product.photo || "";
                      const price =
                        product.pricing?.discountedPrice ||
                        product.pricing?.price ||
                        0;
                      const isHiddenOnMobile =
                        index >= 4 && !shopTheLookShowAll;
                      return (
                        <Link
                          to={`/product/${product._id}`}
                          key={product._id}
                          className={`group cursor-pointer ${isHiddenOnMobile ? "hidden md:block" : ""}`}
                          onClick={() =>
                            trackEvent("product_click", {
                              product_id: product._id,
                              product_name: name,
                              section: "shop_the_look",
                            })
                          }
                        >
                          {/* IMAGE — increased size */}
                          <div className="relative bg-white aspect-[3/4] overflow-hidden mb-4 rounded-lg">
                            <img
                              src={image}
                              alt={name}
                              className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleWishlist(product);
                              }}
                              className="absolute top-3 right-3 bg-white p-1.5 rounded-full shadow-sm"
                            >
                              <Heart
                                className={`w-4 h-4 transition-transform duration-300 ${isInWishlist(product._id) ? "fill-pink-500 text-pink-500" : "text-gray-400"}`}
                                strokeWidth={1.5}
                              />
                            </button>
                          </div>

                          {/* NAME & PRICE */}
                          <p className="text-sm font-medium text-center truncate">
                            {name}
                          </p>
                          <p className="text-sm text-gray-500 text-center">
                            ₹{price}
                          </p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                {/* Show More button for mobile - outside the grid */}
                {shopTheLook.productIds.length > 4 && (
                  <div className="md:hidden mt-4 flex justify-center">
                    <button
                      onClick={() => setShopTheLookShowAll(!shopTheLookShowAll)}
                      className="border border-gray-800 rounded-full px-6 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-all"
                    >
                      {shopTheLookShowAll ? "Show Less" : "Show More"}
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

        {/* Fallback: Show static section if no dynamic config */}
        {!shopTheLookLoading && !shopTheLook && (
          <section className="bg-stone-100 py-8">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900 mb-6">
                SHOP THE LOOK
              </h2>
              {/* MAIN ROW */}
              <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1.35fr] gap-12 items-stretch">
                {/* LEFT : VIDEO (vertical reel style) */}
                <div className="relative w-[280px] md:w-[320px] h-[400px] md:h-[590px] overflow-hidden rounded-xl mx-auto">
                  <video
                    src={reelsVideos[0]}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />

                  {/* OVERLAY */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 text-white bg-gradient-to-t from-black/40 to-transparent">
                    <h3 className="text-2xl md:text-4xl font-light mb-2">
                      A softer start
                    </h3>
                    <p className="text-sm mb-6 tracking-wide">
                      The luxury bedding upgrade you deserve.
                    </p>
                    <button className="bg-orange-400 w-fit text-white px-8 py-3 text-xs tracking-widest uppercase font-semibold hover:bg-orange-500 transition rounded-full">
                      SHOP BEDDING
                    </button>
                  </div>
                </div>

                {/* RIGHT : PRODUCTS (bigger images) */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-10 h-auto md:h-[560px] content-start md:content-between">
                  {beddingProducts.map((product, index) => {
                    const isHiddenOnMobile = index >= 4 && !shopTheLookShowAll;
                    return (
                      <div
                        key={product.id}
                        className={`group cursor-pointer ${isHiddenOnMobile ? "hidden md:block" : ""}`}
                      >
                        {/* IMAGE — increased size */}
                        <div className="relative bg-white aspect-[3/4] overflow-hidden mb-4 rounded-lg">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                          />
                          <button
                            className="absolute top-3 right-3 bg-white p-1.5 rounded-full shadow-sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Heart
                              className="w-4 h-4 text-gray-400"
                              strokeWidth={1.5}
                            />
                          </button>
                        </div>

                        {/* PRICE */}
                        <p className="text-sm font-medium text-center">
                          {product.price}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Show More button for mobile - outside the grid */}
              {beddingProducts.length > 4 && (
                <div className="md:hidden mt-4 flex justify-center">
                  <button
                    onClick={() => setShopTheLookShowAll(!shopTheLookShowAll)}
                    className="border border-gray-800 rounded-full px-6 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-all"
                  >
                    {shopTheLookShowAll ? "Show Less" : "Show More"}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Promotional Banner Section from Code 1 */}
        {promoImages.length > 0 && (
          <div className="bg-white py-6">
            <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                {promoImages[currentPromo] && (
                  <div
                    key={promoImages[currentPromo]._id || currentPromo}
                    className="relative transition-opacity duration-700"
                  >
                    <img
                      src={promoImages[currentPromo].imagePath}
                      alt={promoImages[currentPromo].name}
                      className="w-full h-auto scale-[1.05] sm:scale-100"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <a
                        href={promoImages[currentPromo].link || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-gray-800 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center"
                      >
                        Visit
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Trending Reels Section */}
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
          ref={trendingReelsRef}
        >
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900">
              TRENDING REELS
            </h3>
            <a
              href="/reels"
              className="border border-gray-800 rounded-full px-4 py-1.5 font-semibold text-sm text-gray-900 bg-white hover:bg-gray-100 transition-all flex items-center"
            >
              View All <ChevronRight size={16} className="ml-1" />
            </a>
          </div>

          <div className="text-center mb-4">
            <p className="text-gray-600 mt-1 text-sm">
              Your kind of people. Your kind of style — styled so good, we had
              to post it.
            </p>
            <button className="text-cyan-500 text-sm font-semibold underline mt-1 hover:text-cyan-600 transition-colors">
              Get Featured on Our Reels
            </button>
          </div>

          <div className="relative">
            {/* Scroll Left Button */}
            <button
              onClick={() => {
                const container = document.getElementById("trending-container");
                const cardWidth =
                  container.querySelector("div").offsetWidth + 24;
                container.scrollBy({ left: -cardWidth, behavior: "smooth" });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all -ml-4 hidden md:block"
            >
              <ChevronLeft size={24} className="text-gray-800" />
            </button>

            {/* Scroll Right Button */}
            <button
              onClick={() => {
                const container = document.getElementById("trending-container");
                const cardWidth =
                  container.querySelector("div").offsetWidth + 24;
                container.scrollBy({ left: cardWidth, behavior: "smooth" });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all -mr-4 hidden md:block"
            >
              <ChevronRight size={24} className="text-gray-800" />
            </button>

            <div
              id="trending-container"
              className="flex overflow-x-auto scrollbar-hide gap-3 md:gap-4 pb-4 relative"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {Array.from({ length: 6 }).map((_, i) => {
                const reel = reels[i];
                if (reel) {
                  return (
                    <div
                      key={reel._id || i}
                      className="relative w-40 md:w-56 h-56 md:h-80 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer flex-shrink-0"
                      style={{ minWidth: "10rem" }}
                      onClick={() => {
                        trackEvent("reel_click", { reel_id: reel._id });
                        navigate("/reels", { state: { scrollToReelId: reel._id } });
                      }}
                    >
                      {/* Always play video in this section */}
                      <video
                        src={reel.videoUrl}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        autoPlay
                      />
                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      {/* Reel title */}
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-white text-sm font-medium truncate">
                          {reel.productName || "Lifestyle Reel"}
                        </p>
                      </div>
                      {/* Play icon overlay on hover */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/30 backdrop-blur-sm rounded-full p-3">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Blank reel card
                  return (
                    <div
                      key={i}
                      className="relative w-40 md:w-56 h-56 md:h-80 rounded-xl overflow-hidden shadow-md flex items-center justify-center bg-gray-100 flex-shrink-0"
                      style={{ minWidth: "10rem" }}
                    >
                      <span className="text-gray-400 text-base md:text-lg font-semibold text-center px-4">
                        No Reel
                      </span>
                    </div>
                  );
                }
              })}
              {/* View more reels card (always after last reel) */}
              <div
                className="relative w-40 md:w-56 h-56 md:h-80 rounded-xl overflow-hidden shadow-md flex items-center justify-center bg-gray-100 flex-shrink-0 cursor-pointer hover:bg-gray-200 transition"
                style={{ minWidth: "10rem" }}
                onClick={() => trackEvent("reel_section_opened")}
              >
                <span className="text-gray-600 text-base md:text-lg font-semibold text-center px-4">
                  View more reels...
                </span>
              </div>
            </div>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => {
                const container = document.getElementById("trending-container");
                if (container) {
                  const cardWidth =
                    container.querySelector("div")?.offsetWidth || 160;
                  container.scrollBy({ left: cardWidth, behavior: "smooth" });
                }
              }}
              className="block md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-transparent border-none p-0 m-0"
              style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.15))" }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="16"
                  fill="white"
                  fillOpacity="0.85"
                />
                <path
                  d="M13 10l5 6-5 6"
                  stroke="#222"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </section>

        {/* Shop The Look Image (Dynamic) */}
        {/* {shopTheLookImage &&
          shopTheLookImage.imageUrl &&
          shopTheLookImage.productIds?.length > 0 && (
            <section className="bg-gray-50 py-16">
              <div className="max-w-7xl mx-auto px-4">
                <h2 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900 mb-6">
                  SHOP THE LOOK
                </h2> */}
                {/* MAIN ROW */}
                {/* <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1.35fr] gap-12 items-stretch"> */}
                  {/* LEFT : VIDEO (fallback) */}
                  {/* <div className="relative h-[400px] md:h-[590px] overflow-hidden rounded-xl">
                    <video
                      src={reelsVideos[1]}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    /> */}

                    {/* OVERLAY CONTENT */}
                    {/* <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 text-white bg-gradient-to-t from-black/40 to-transparent">
                      <h3 className="text-2xl md:text-4xl lg:text-5xl mb-2 font-light">
                        Winter icons
                      </h3>
                      <p className="text-sm mb-6 tracking-wide">
                        Effortless warmth, exceptional craft.
                      </p>
                      <button className="bg-orange-400 w-fit text-white px-8 py-3 text-xs tracking-widest uppercase font-semibold hover:bg-orange-500 transition rounded-full">
                        SHOP NOW
                      </button>
                    </div>
                  </div> */}

                  {/* RIGHT : PRODUCTS (fallback with static winterProducts) */}
                  {/* <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-10 h-auto md:h-[560px] content-start md:content-between">
                    {winterProducts.map((product, index) => {
                      const isHiddenOnMobile =
                        index >= 4 && !shopTheLookImageShowAll;
                      return (
                        <div
                          key={product.id}
                          className={`group cursor-pointer ${isHiddenOnMobile ? "hidden md:block" : ""}`}
                        > */}
                          {/* PRODUCT IMAGE */}
                          {/* <div className="relative bg-white aspect-[3/4] overflow-hidden mb-4 rounded-lg">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                            />
                            <button
                              className="absolute top-3 right-3 bg-white p-1.5 rounded-full shadow-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            > */}
                              {/* <Heart
                                className="w-4 h-4 text-gray-400"
                                strokeWidth={1.5}
                              />
                            </button>
                          </div> */}

                          {/* PRICE */}
                          {/* <p className="text-sm font-medium text-center">
                            {product.price}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div> */}
                {/* Show More button for mobile - outside the grid */}
                {/* {winterProducts.length > 4 && (
                  <div className="md:hidden mt-4 flex justify-center">
                    <button
                      onClick={() =>
                        setShopTheLookImageShowAll(!shopTheLookImageShowAll)
                      }
                      className="border border-gray-800 rounded-full px-6 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-all"
                    >
                      {shopTheLookImageShowAll ? "Show Less" : "Show More"}
                    </button>
                  </div>
                )}
              </div> */}
            {/* </section>
          )} */}

        {/* Blog Section */}
        <div className="bg-gray-50 py-12" ref={blogsRef}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900">
                LATEST BLOGS
              </h3>
              <Link
                to="/blogs"
                className="border border-gray-800 rounded-full px-4 py-1.5 font-semibold text-sm text-gray-900 bg-white hover:bg-pink-500 hover:text-white hover:border-pink-500 transition-all flex items-center"
              >
                View All <ChevronRight size={16} className="ml-1" />
              </Link>
            </div>

            {/* Blog Cards with Navigation */}
            <div className="relative group">
              {/* Left Arrow - Show only if we can scroll left */}
              {blogStartIndex > 0 && (
                <button
                  onClick={() =>
                    setBlogStartIndex(
                      Math.max(0, blogStartIndex - blogsPerView),
                    )
                  }
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all -ml-4 hidden md:block"
                >
                  <ChevronLeft size={24} className="text-gray-800" />
                </button>
              )}

              {/* Right Arrow - Show only if there are more blogs */}
              {blogStartIndex + blogsPerView < blogs.length && (
                <button
                  onClick={() =>
                    setBlogStartIndex(
                      Math.min(
                        blogs.length - blogsPerView,
                        blogStartIndex + blogsPerView,
                      ),
                    )
                  }
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all -mr-4 hidden md:block"
                >
                  <ChevronRight size={24} className="text-gray-800" />
                </button>
              )}

              {/* Mobile: Horizontal Scroll | Desktop: Grid */}
              <div
                className="overflow-x-auto md:overflow-visible pb-2 no-scrollbar"
                style={{ scrollbarWidth: "none" }}
              >
                <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {blogs.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500">
                      No blogs available yet
                    </div>
                  ) : (
                    blogs
                      .slice(blogStartIndex, blogStartIndex + blogsPerView)
                      .map((blog) => (
                        <div
                          key={blog._id}
                          className="flex-shrink-0 w-[85%] md:w-auto bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
                          onClick={() => navigate(`/blog/${blog.slug}`)}
                        >
                          {/* Blog Image */}
                          <div className="relative h-40 md:h-48 overflow-hidden bg-gray-200">
                            {blog.image ? (
                              <img
                                src={blog.image}
                                alt={blog.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://via.placeholder.com/400x300?text=No+Image";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                No Image
                              </div>
                            )}
                          </div>

                          {/* Blog Content */}
                          <div className="p-4 md:p-5">
                            <p className="text-xs text-gray-500 mb-2">
                              {new Date(blog.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                            </p>
                            <h3 className="text-base font-bold text-gray-800 mb-2 line-clamp-2 min-h-[3rem]">
                              {blog.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-4 line-clamp-3 min-h-[4rem]">
                              {blog.short_description ||
                                blog.full_description.substring(0, 150) + "..."}
                            </p>
                            <button className="flex items-center text-sm font-semibold text-gray-800 hover:text-cyan-500 transition-colors group">
                              Read More
                              <svg
                                className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div id="customer-reviews" className="bg-white py-12" ref={reviewsRef}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-normal uppercase tracking-wide text-lg md:text-2xl text-gray-900">
                Customer Reviews
              </h3>
              <button className="border border-gray-800 rounded-full px-4 py-1.5 font-semibold text-sm text-gray-900 bg-white hover:bg-gray-100 transition-all flex items-center">
                View All <ChevronRight size={16} className="ml-1" />
              </button>
            </div>

            <div className="relative">
              {/* Scroll Left Button - Hidden on mobile */}
              <button
                onClick={() => {
                  const container =
                    document.getElementById("reviews-container");
                  container.scrollBy({ left: -900, behavior: "smooth" });
                }}
                className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all -ml-4"
              >
                <ChevronLeft size={24} className="text-gray-800" />
              </button>

              {/* Scroll Right Button - Hidden on mobile */}
              <button
                onClick={() => {
                  const container =
                    document.getElementById("reviews-container");
                  container.scrollBy({ left: 900, behavior: "smooth" });
                }}
                className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-all -mr-4"
              >
                <ChevronRight size={24} className="text-gray-800" />
              </button>

              {/* Horizontal Scrollable Container */}
              <div
                id="reviews-container"
                className="flex overflow-x-auto scrollbar-hide gap-4 md:gap-8 pb-4 snap-x snap-mandatory"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {[
                  {
                    image:
                      "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600",
                    customer: "Sarah Johnson",
                    product: "Wireless Headphones",
                    rating: 5,
                    review:
                      "boAt is the first company from the consumer lifestyle electronics industry to collaborate with the ICEA to bring out the Indigenous IP.",
                  },
                  {
                    image:
                      "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600",
                    customer: "Michael Chen",
                    product: "Smart Watch Pro",
                    rating: 5,
                    review:
                      "The quality and design exceeded my expectations. The battery life is incredible and it pairs seamlessly with my phone. Highly recommended!",
                  },
                  {
                    image:
                      "https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600",
                    customer: "Emma Williams",
                    product: "Fashion Dress",
                    rating: 4,
                    review:
                      "Beautiful dress with excellent fabric quality. The fit is perfect and I received so many compliments. Will definitely order more from this brand.",
                  },
                  {
                    image:
                      "https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=600",
                    customer: "David Martinez",
                    product: "Running Shoes",
                    rating: 5,
                    review:
                      "Most comfortable shoes I've ever owned. Great for long runs and the cushioning is perfect. The style is also very trendy and modern.",
                  },
                  {
                    image:
                      "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600",
                    customer: "Lisa Anderson",
                    product: "Designer Handbag",
                    rating: 5,
                    review:
                      "Absolutely love this bag! The leather quality is outstanding and it's spacious enough for all my daily essentials. Worth every penny!",
                  },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 w-[85vw] sm:w-[80vw] md:w-[700px] lg:w-[900px] bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 snap-center"
                  >
                    <div className="flex flex-col md:flex-row h-full">
                      {/* Left Side - Product Image */}
                      <div className="w-full md:w-2/5 bg-gray-200 p-4 md:p-8 flex items-center justify-center">
                        <div className="relative">
                          <img
                            src={review.image}
                            alt={review.product}
                            className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 object-contain rounded-lg"
                          />
                        </div>
                      </div>

                      {/* Right Side - Review Content */}
                      <div className="w-full md:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                        {/* Quote Icon */}
                        <div className="mb-3 md:mb-4">
                          <svg
                            className="w-8 h-8 md:w-12 md:h-12 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                          </svg>
                        </div>

                        {/* Review Text */}
                        <p className="text-gray-700 text-base md:text-lg mb-4 md:mb-6 leading-relaxed italic">
                          "{review.review}"
                        </p>

                        {/* Rating Stars */}
                        <div className="flex items-center mb-3 md:mb-4">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-4 h-4 md:w-5 md:h-5 ${
                                i < review.rating
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>

                        {/* Customer Info */}
                        <div
                          className="w-full bg-white border-t border-b border-gray-200 relative overflow-hidden usp-marquee-thin"
                          style={{ minHeight: "48px" }}
                        >
                          <p className="text-gray-500 text-xs md:text-sm">
                            Purchased: {review.product}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </section>
      <BottomNavBar />
    </div>
  );
};

export default FerreaFashion;
